import type { Course, CourseListFilters, SortOrder } from 'services/courses'
import type { DashboardData } from 'services/dashboard'
import type { Provider } from 'services/providers'
import type { UserProfile } from 'services/profile'
import { DEMO_LATENCY_MS } from './config'
import { demoCourses, demoDashboard, demoProfile, demoProviders } from './data'

/**
 * In-memory stand-in for the Firestore reads the app performs in demo mode.
 *
 * The filtering here mirrors `buildCoursesQueryConstraints` in
 * `services/courses.ts`. Keeping the two in step matters: if the demo list
 * silently ignores the `archive` filter, every screen reviewed against it is
 * being reviewed with the wrong data.
 */

const delay = <T>(value: T): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), DEMO_LATENCY_MS))

const toTime = (value: string | Date) => new Date(value).getTime()

function applyCourseFilters(filters: CourseListFilters, order?: SortOrder): Course[] {
  let results = [...demoCourses]

  if (filters.providerID) {
    results = results.filter((course) => course.provider === filters.providerID)
  }

  // Firestore stores `archive` explicitly, so an absent flag reads as false.
  if (filters.archive === true) {
    results = results.filter((course) => course.archive === true)
  } else if (filters.archive === false) {
    results = results.filter((course) => (course.archive ?? false) === false)
  }

  if (filters.fromDate) {
    const from = filters.fromDate.getTime()
    results = results.filter((course) => toTime(course.date) >= from)
  }

  if (filters.toDate) {
    const to = filters.toDate.getTime()
    results = results.filter((course) => toTime(course.date) <= to)
  }

  if (order) {
    results.sort((a, b) => (order === 'asc' ? toTime(a.date) - toTime(b.date) : toTime(b.date) - toTime(a.date)))
  }

  // `startAfter` is cursor pagination on the ordered `date` field.
  if (typeof filters.startAfter === 'string') {
    const cursor = toTime(filters.startAfter)
    results = results.filter((course) =>
      order === 'desc' ? toTime(course.date) < cursor : toTime(course.date) > cursor,
    )
  }

  if (filters.limit) {
    results = results.slice(0, filters.limit)
  }

  return results
}

export const demoStore = {
  listCourses: (filters: CourseListFilters = {}, order?: SortOrder) => delay(applyCourseFilters(filters, order)),

  countCourses: (filters: CourseListFilters = {}) => {
    // A count ignores pagination — it reports the size of the whole match.
    const { limit, startAfter, ...rest } = filters
    void limit
    void startAfter
    return delay(applyCourseFilters(rest).length)
  },

  getCourse: (courseID: string): Promise<Course | undefined> =>
    delay(demoCourses.find((course) => course.id === courseID)),

  listProviders: (): Promise<Provider[]> =>
    delay([...demoProviders].sort((a, b) => a.name.localeCompare(b.name))),

  getProvider: (providerID: string): Promise<Provider | undefined> =>
    delay(demoProviders.find((provider) => provider.id === providerID)),

  getDashboard: (): Promise<DashboardData> => delay(demoDashboard),

  getProfile: (): Promise<UserProfile> => delay(demoProfile),
}
